import { useState } from "react";
import useAppMain from "src/hooks/useAppMain";
import { CLIENT_TAB_LIST, PATH_NAMES } from "src/utils/constants";
import {
  Text,
  Box,
  Flex,
  useMantineTheme,
  Divider,
  Skeleton,
  Button,
} from "@mantine/core";
import { useNavigate, useParams } from "react-router-dom";
import {
  useCheckForSignedAgreementQuery,
  useCreateAgreementRecipientMutation,
  useCreateSignedAgreementMutation,
  useLocationOfferingOneQuery,
} from "src/generated/graphql";
import { useAppContext } from "src/api/AppContext";

function AgreementOverview() {
  const theme = useMantineTheme();
  useAppMain(CLIENT_TAB_LIST.AGREEMENT_OVERVIEW, true);
  const { locationOfferingId = "" } = useParams();
  const { currentClient } = useAppContext();
  const primaryTextColor = theme.colors.primaryTextColor[6];
  const cardBg = theme.colors.primary[4];
  const navigate = useNavigate();

  const { loading, data: offeringDetail } = useLocationOfferingOneQuery({
    variables: {
      id: locationOfferingId,
    },
    skip: !locationOfferingId,
  });
  const { agreement, location } = offeringDetail?.locationOfferingOne || {};
  const {
    id: agreementId,
    name,
    description,
    locations,
    recurringCharges,
    initialCharges,
    additionalPolicies,
  } = agreement || {};
  const [signedAgreementId, setsignedAgreementId] = useState("");

  // check for signed agreement already exists
  useCheckForSignedAgreementQuery({
    variables: {
      agreementId: agreementId || "",
      clientId: currentClient?.id || "",
      locationId: location?.id || "",
    },
    skip: !agreementId || !currentClient?.id || !location?.id,
    onCompleted(data) {
      setsignedAgreementId(data.checkForSignedAgreement?.id || "");
    },
    fetchPolicy: "network-only",
  });

  const [signPolicies, { loading: signPoliciesLoading }] =
    useCreateAgreementRecipientMutation({
      onCompleted: () => {
        handleContinue();
      },
    });

  const [createSignedAgreement, { loading: signedAgreementLoading }] =
    useCreateSignedAgreementMutation({
      onCompleted: (data) => {
        setsignedAgreementId(data.createSignedAgreement.id);
        signPolicies({
          variables: {
            data: {
              client: { id: currentClient!.id },
              signedAgreement: { id: data.createSignedAgreement.id },
            },
          },
        });
      },
    });

  const handleSignPolicies = () => {
    if (!agreementId || !location?.id) return;
    createSignedAgreement({
      variables: {
        data: {
          agreement: { id: agreementId },
          client: { id: currentClient!.id },
          location: {
            id: location.id,
          },
        },
      },
    });
  };

  const handleContinue = () => {
    navigate(`/${PATH_NAMES.AGREEMENT_DOCUMENT}/${signedAgreementId}`);
  };

  const handleSubmit = () => {
    // signedAgreementId already exists then simply navigate to document else sign
    signedAgreementId ? handleContinue() : handleSignPolicies();
  };

  if (loading) return <Skeleton w="100%" height={80} />;
  return (
    <Flex
      direction={"column"}
      justify={"space-between"}
      sx={{ height: "100%" }}
    >
      <Box>
        <Box bg={cardBg} p={12} sx={{ borderRadius: 8 }}>
          <Text fw={500} size={18} color={primaryTextColor}>
            {name}
          </Text>
          <Box px={"xs"}>
            <Text color={primaryTextColor}>{description}</Text>
          </Box>

          <Box pt={"xs"}>
            {locations?.map((loc) => (
              <Box key={loc.id}>
                <Text fw={500} size={18} color={primaryTextColor}>
                  {loc.name}
                </Text>
                <Box px={"xs"}>
                  <Text color={primaryTextColor}>{loc.address}</Text>
                  <Text color={primaryTextColor}>
                    {loc.primaryContact?.phoneNumber}
                  </Text>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>

        <Box bg={cardBg} p={12} mt={"xs"} sx={{ borderRadius: 8 }}>
          <Box>
            <Text fw={500} size={18} color={primaryTextColor}>
              Recurring Costs
            </Text>
            {!recurringCharges?.length && (
              <Text fw={500} size={14} align="center" color={primaryTextColor}>
                No Recurring available
              </Text>
            )}
            {recurringCharges?.map((r) => (
              <Flex key={r.id} justify={"space-between"} px={"xs"}>
                <Text color={primaryTextColor}>{r.name}</Text>
                <Text color={primaryTextColor}>{r.cost}</Text>
              </Flex>
            ))}
          </Box>

          <Divider my={7} color={primaryTextColor} />

          <Box>
            <Text fw={500} size={18} color={primaryTextColor}>
              Sign-up Costs
            </Text>
            {!initialCharges?.length && (
              <Text fw={500} size={14} align="center" color={primaryTextColor}>
                No Sign-up Costs available
              </Text>
            )}
            {initialCharges?.map((r) => (
              <Flex key={r.id} justify={"space-between"} px={"xs"}>
                <Text color={primaryTextColor}>{r.revenue?.name}</Text>
                <Text color={primaryTextColor}>{r.cost}</Text>
              </Flex>
            ))}
          </Box>
        </Box>

        <Box bg={cardBg} p={12} mt={"sm"} sx={{ borderRadius: 8 }}>
          <Box>
            <Text fw={500} size={18} color={primaryTextColor}>
              Policies
            </Text>
            {!additionalPolicies?.length && (
              <Text fw={500} size={14} align="center" color={primaryTextColor}>
                No Policies available
              </Text>
            )}
            {additionalPolicies?.map((policy) => (
              <Text px={"xs"} key={policy.id} color={primaryTextColor}>
                {policy.name}
              </Text>
            ))}
          </Box>
        </Box>
      </Box>

      <Button
        onClick={handleSubmit}
        loading={signedAgreementLoading || signPoliciesLoading}
        disabled={!currentClient?.id}
      >
        Continue to Policy Review
      </Button>
    </Flex>
  );
}

export default AgreementOverview;
